import type {
  DebriefRequest,
  DebriefResponse,
  InterviewSetup,
  CreateSessionResponse,
  MaterialExtractResponse,
  ProgressReportListResponse,
  ProgressReportRecord,
  SessionListResponse,
  SessionRecord,
  SessionTranscriptBody,
  RealtimeCredential,
} from './types';

interface ApiErrorShape {
  error?: {
    code?: string;
    message?: string;
  };
}

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;

  constructor(message: string, status: number, code = 'UNKNOWN') {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
  }
}

async function readJson<T>(response: Response): Promise<T> {
  let data: unknown;
  try {
    data = await response.json();
  } catch {
    throw new ApiError('The server returned an unreadable response.', response.status);
  }
  if (!response.ok) {
    const error = data as ApiErrorShape;
    throw new ApiError(
      error.error?.message || 'The request failed. Try again.',
      response.status,
      error.error?.code,
    );
  }
  return data as T;
}

export async function authStatus(): Promise<boolean> {
  const response = await fetch('/api/auth/status', {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });
  const data = await readJson<{ authenticated: boolean }>(response);
  return data.authenticated;
}

export async function login(accessCode: string): Promise<void> {
  const response = await fetch('/api/auth/login', {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ accessCode }),
  });
  await readJson<{ authenticated: true }>(response);
}

export async function logout(): Promise<void> {
  const response = await fetch('/api/auth/logout', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });
  await readJson<{ authenticated: false }>(response);
}

/** Persisted uploads carry UI-only fields (e.g. `truncated`); the API gets the contract shape only. */
export function toWireSetup(setup: InterviewSetup): InterviewSetup {
  return {
    ...setup,
    materials: setup.materials.map(({ id, kind, name, text }) => ({ id, kind, name, text })),
  };
}

export async function createRealtimeCredential(
  setup: InterviewSetup,
): Promise<RealtimeCredential> {
  const response = await fetch('/api/realtime/session', {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(toWireSetup(setup)),
  });
  return readJson<RealtimeCredential>(response);
}

export async function extractMaterial(file: File): Promise<MaterialExtractResponse> {
  const body = new FormData();
  body.append('file', file);
  const response = await fetch('/api/materials/extract', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
    body,
  });
  return readJson<MaterialExtractResponse>(response);
}

export async function createDebrief(
  request: DebriefRequest,
  signal?: AbortSignal,
): Promise<DebriefResponse> {
  const response = await fetch('/api/debrief', {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ ...request, setup: toWireSetup(request.setup) }),
    signal,
  });
  return readJson<DebriefResponse>(response);
}

export async function fetchContent(): Promise<unknown> {
  const response = await fetch('/api/content', {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });
  return readJson<unknown>(response);
}

/** Debrief-only: what a built-in candidate was hiding, as markdown/plain text. */
export async function fetchHiddenTruths(profile: string): Promise<string> {
  const response = await fetch(`/api/content/hidden?profile=${encodeURIComponent(profile)}`, {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });
  const data = await readJson<unknown>(response);
  const value = typeof data === 'string' ? data : (data as { hiddenTruths?: unknown }).hiddenTruths;
  const text = Array.isArray(value) ? value.map((item) => `- ${String(item)}`).join('\n') : String(typeof value === 'string' ? value : '');
  return text
    .split('\n')
    .filter((line) => !line.trim().startsWith('PRIVATE FACTS'))
    .join('\n')
    .trim();
}


/* Server-side sessions (D1). Every call needs the login cookie. */

async function sendJson<T>(method: string, url: string, body?: unknown, signal?: AbortSignal): Promise<T> {
  const response = await fetch(url, {
    method,
    credentials: 'same-origin',
    headers: body === undefined ? { Accept: 'application/json' } : { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
    signal,
  });
  return readJson<T>(response);
}

export const createSession = (setup: InterviewSetup) =>
  sendJson<CreateSessionResponse>('POST', '/api/sessions', { setup: toWireSetup(setup) });
export const saveSessionTranscript = (id: string, body: SessionTranscriptBody) =>
  sendJson<{ ok: true }>('PUT', `/api/sessions/${encodeURIComponent(id)}/transcript`, body);
export const finishSession = (id: string, body: SessionTranscriptBody) =>
  sendJson<SessionRecord>('POST', `/api/sessions/${encodeURIComponent(id)}/finish`, body);
export const listSessions = (signal?: AbortSignal) =>
  sendJson<SessionListResponse>('GET', '/api/sessions', undefined, signal);
export const getSession = (id: string, signal?: AbortSignal) =>
  sendJson<SessionRecord>('GET', `/api/sessions/${encodeURIComponent(id)}`, undefined, signal);
export const regenerateSession = (id: string) =>
  sendJson<SessionRecord>('POST', `/api/sessions/${encodeURIComponent(id)}/regenerate`, {});
export const deleteSession = (id: string) =>
  sendJson<{ ok: true }>('DELETE', `/api/sessions/${encodeURIComponent(id)}`);
export const createProgressReport = (sessionIds?: string[]) =>
  sendJson<ProgressReportRecord>('POST', '/api/progress/reports', sessionIds ? { sessionIds } : {});
export const listProgressReports = (signal?: AbortSignal) =>
  sendJson<ProgressReportListResponse>('GET', '/api/progress/reports', undefined, signal);
export const getProgressReport = (id: string, signal?: AbortSignal) =>
  sendJson<ProgressReportRecord>('GET', `/api/progress/reports/${encodeURIComponent(id)}`, undefined, signal);
